import { SurveyModel } from "survey-core";
import { useEffect, useRef, useState } from "react";
import { cn } from "~/lib/utils";

interface Props {
    survey: SurveyModel | null;
    className?: string;
    /** Fills the bar, for the page that ends the survey. */
    isFull?: boolean;
}

export const ProgressBar = ({ survey, className, isFull = false }: Props) => {
    const [progress, setProgress] = useState(0);
    const lastPagePosition = useRef<number | null>(null);

    useEffect(() => {
        if (!survey) return;

        const update = () => {
            if (!survey || survey.visiblePages.length === 0 || !survey.currentPage) return;
            const completedPages = survey.currentPageNo;
            const newProgress = (completedPages / survey.visiblePages.length) * 100;
            // Direction comes from the page's position in the schema rather than among the
            // visible pages, because conditional pages appearing or disappearing shift the
            // visible index without the user navigating anywhere.
            const pagePosition = survey.pages.indexOf(survey.currentPage);
            const wentBack = lastPagePosition.current !== null && pagePosition < lastPagePosition.current;

            lastPagePosition.current = pagePosition;

            // A revealed page grows the denominator and a hidden earlier page shrinks the index,
            // either of which lowers the ratio while the user stands still. Hold the previous
            // value so the bar only ever retreats when the user actually navigates back.
            setProgress(progress => wentBack ? newProgress : Math.max(progress, newProgress));
        };

        update();

        survey.onCurrentPageChanged.add(update);
        survey.onValueChanged.add(update);
        survey.onComplete.add(update);

        return () => {
            survey.onCurrentPageChanged.remove(update);
            survey.onValueChanged.remove(update);
            survey.onComplete.remove(update);
        };
    }, [survey]);

    if (!survey || survey.showProgressBar) return null;

    return (
        <div className={cn("h-1 border-b border-b-[#d1d5dc]", className)}>
            <div className="h-1 bg-primary400 duration-300" style={{ width: `${isFull ? 100 : progress}%` }} />
        </div>
    );
}

export default ProgressBar;
